'use client';

import Image from 'next/image';
import {
  Eye,
  EyeOff,
  ImagePlus,
  LoaderCircle,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { uploadHomepageImage } from '../lib/homepage-config';
import {
  archivePortalGame,
  upsertPortalGame,
  type ManagedPortalGame,
  type PortalConfig,
} from '../lib/portal-config';
import { readWikiToken } from '../lib/wiki-auth';
import PortalPermissionManager from './portal-permission-manager';
import './portal-admin-editor.css';

const reservedDomains = new Set([
  'api',
  'wikis',
  'updates',
  'characters',
  'profile',
  'starrail-demo',
  'moon-demo',
  'observatory-demo',
  'cloud-demo',
  'echo-demo',
  'flower-demo',
  'dawn-demo',
]);
type Draft = { name: string; icon: string; banner: string; domain: string };
type FormMode = 'create' | 'edit' | null;
type Props = {
  config: PortalConfig;
  open: boolean;
  onClose: () => void;
  onChange: (config: PortalConfig) => void;
};

export default function PortalAdminEditor({
  config,
  open,
  onClose,
  onChange,
}: Props) {
  const fileInput = useRef<HTMLInputElement>(null);
  const bannerInput = useRef<HTMLInputElement>(null);
  const [formMode, setFormMode] = useState<FormMode>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>({
    name: '',
    icon: '',
    banner: '',
    domain: '',
  });
  const [uploading, setUploading] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'home' | 'banners' | 'permissions'>('home');
  const domain = draft.domain.trim().toLowerCase();
  const domainValid = /^(?=.*[a-z])[a-z0-9-]{2,32}$/.test(domain);
  const domainTaken =
    reservedDomains.has(domain) ||
    config.games.some((game) => game.id === domain && game.id !== editingId);

  const requireToken = () => {
    const token = readWikiToken();
    if (!token) {
      window.alert('请重新登录');
      return null;
    }
    return token;
  };

  const applyLocalGame = (game: ManagedPortalGame, previousId?: string | null) => {
    const replaceId = previousId || game.id;
    const index = config.games.findIndex((item) => item.id === replaceId);
    if (index < 0) {
      onChange({ games: [...config.games, game] });
      return;
    }
    const games = config.games.slice();
    games[index] = game;
    onChange({
      games:
        previousId && previousId !== game.id
          ? games.filter((item, i) => i === index || item.id !== game.id)
          : games,
    });
  };

  const openCreate = () => {
    setEditingId(null);
    setDraft({ name: '', icon: '', banner: '', domain: '' });
    setStep(1);
    setFormMode('create');
  };
  const openEdit = (id: string) => {
    const game = config.games.find((item) => item.id === id);
    if (!game) return;
    setEditingId(id);
    setDraft({
      name: game.name,
      icon: game.icon,
      banner: game.banner || '',
      domain: game.id,
    });
    setStep(1);
    setFormMode('edit');
  };
  const closeForm = () => {
    if (!saving) setFormMode(null);
  };
  const upload = async (file?: File) => {
    if (!file) return;
    const token = requireToken();
    if (!token) return;
    setUploading(true);
    try {
      const icon = await uploadHomepageImage('platform', file, token);
      setDraft((value) => ({ ...value, icon }));
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Icon 上传失败');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };
  const uploadBanner = async (file?: File) => {
    if (!file) return;
    const token = requireToken();
    if (!token) return;
    setBannerUploading(true);
    try {
      const banner = await uploadHomepageImage('platform', file, token);
      setDraft((value) => ({ ...value, banner }));
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Banner 上传失败');
    } finally {
      setBannerUploading(false);
      if (bannerInput.current) bannerInput.current.value = '';
    }
  };
  const submit = async () => {
    if (
      !formMode ||
      !domainValid ||
      domainTaken ||
      !draft.name.trim() ||
      !draft.icon
    )
      return;
    const token = requireToken();
    if (!token) return;
    const existing = config.games.find((item) => item.id === editingId);
    const game: ManagedPortalGame = {
      ...existing,
      key: existing?.key || domain,
      id: domain,
      name: draft.name.trim(),
      icon: draft.icon,
      banner: draft.banner.trim(),
      hidden: existing?.hidden,
    };
    setSaving(true);
    try {
      await upsertPortalGame(game, token);
      applyLocalGame(game, formMode === 'edit' ? editingId : null);
      setFormMode(null);
    } catch (error) {
      window.alert(error instanceof Error ? error.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };
  const toggleHidden = async (id: string) => {
    const token = requireToken();
    if (!token) return;
    const current = config.games.find((game) => game.id === id);
    if (!current) return;
    const next = { ...current, hidden: !current.hidden };
    setSaving(true);
    try {
      await upsertPortalGame(next, token);
      applyLocalGame(next);
    } catch (error) {
      window.alert(error instanceof Error ? error.message : '更新失败');
    } finally {
      setSaving(false);
    }
  };
  const remove = async (id: string, name: string) => {
    if (!window.confirm(`确定删除“${name}”吗？删除后该 Wiki 将无法访问。`))
      return;
    const token = requireToken();
    if (!token) return;
    setSaving(true);
    try {
      await archivePortalGame(id, token);
      onChange({ games: config.games.filter((game) => game.id !== id) });
    } catch (error) {
      window.alert(error instanceof Error ? error.message : '删除失败');
    } finally {
      setSaving(false);
    }
  };
  if (!open) return null;

  return (
    <>
      <button
        className="portal-admin-backdrop"
        type="button"
        onClick={onClose}
        aria-label="关闭首页管理"
      />
      <aside className="portal-admin-drawer" aria-label="平台管理">
        <header className="portal-admin-heading">
          <div>
            <small>PORTAL MANAGEMENT</small>
            <h2>平台管理</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="关闭">
            <X />
          </button>
        </header>
        <nav className="portal-admin-tabs" aria-label="平台管理模块">
          <button className={activeTab === 'home' ? 'is-active' : ''} type="button" onClick={() => setActiveTab('home')}>首页管理</button>
          <button className={activeTab === 'banners' ? 'is-active' : ''} type="button" onClick={() => setActiveTab('banners')}>横幅配置</button>
          <button className={activeTab === 'permissions' ? 'is-active' : ''} type="button" onClick={() => setActiveTab('permissions')}>权限管理</button>
        </nav>
        {activeTab === 'home' && <><div className="portal-admin-drawer-actions">
          <div>
            <strong>游戏列表</strong>
            <p>管理聚合页中的游戏 Wiki。</p>
          </div>
          <button type="button" onClick={openCreate}>
            <Plus />
            添加游戏
          </button>
        </div>
        <div className="portal-admin-game-list">
          {config.games.map((game) => (
            <article className={game.hidden ? 'is-hidden' : ''} key={game.id}>
              <Image
                src={game.icon}
                width={52}
                height={52}
                unoptimized
                alt=""
              />
              <div>
                <strong>{game.name}</strong>
                <span>/{game.id}</span>
                <small>{game.hidden ? '已隐藏' : '展示中'}</small>
              </div>
              <nav aria-label={`${game.name}管理操作`}>
                <button
                  type="button"
                  onClick={() => openEdit(game.id)}
                  title="编辑"
                >
                  <Pencil />
                </button>
                <button
                  type="button"
                  onClick={() => void toggleHidden(game.id)}
                  title={game.hidden ? '显示' : '隐藏'}
                >
                  {game.hidden ? <Eye /> : <EyeOff />}
                </button>
                <button
                  className="danger"
                  type="button"
                  onClick={() => void remove(game.id, game.name)}
                  title="删除"
                >
                  <Trash2 />
                </button>
              </nav>
            </article>
          ))}
          {!config.games.length && (
            <div className="portal-admin-empty">
              <ImagePlus />
              <p>还没有创建游戏 Wiki</p>
              <button type="button" onClick={openCreate}>
                添加第一个游戏
              </button>
            </div>
          )}
        </div></>}
        {activeTab === 'banners' && <>
          <div className="portal-admin-drawer-actions">
            <div><strong>头部 Wiki 横幅</strong><p>为游戏配置首页轮播大图。</p></div>
          </div>
          <div className="portal-admin-game-list">
            {config.games.map((game) => (
              <article key={game.id}>
                <img src={game.banner || game.icon} width={52} height={52} alt="" />
                <div><strong>{game.name}</strong><small>{game.banner ? '已配置横幅' : '未配置横幅'}</small></div>
                <nav><button type="button" onClick={() => openEdit(game.id)}>配置横幅</button></nav>
              </article>
            ))}
          </div>
        </>}
        {activeTab === 'permissions' && <PortalPermissionManager games={config.games} active />}
        <footer>
          <span>{activeTab !== 'permissions' ? `共 ${config.games.length} 个游戏` : '权限变更实时生效'}</span>
          <button type="button" onClick={onClose}>
            完成
          </button>
        </footer>
      </aside>

      {formMode && (
        <div
          className="portal-game-form-layer"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeForm();
          }}
        >
          <dialog
            open
            className="portal-game-form"
            aria-label={formMode === 'edit' ? '编辑游戏' : '添加游戏'}
          >
            <header>
              <div>
                <small>步骤 {step} / 2</small>
                <h2>{formMode === 'edit' ? '编辑游戏' : '创建游戏'}</h2>
              </div>
              <button type="button" onClick={closeForm} aria-label="关闭">
                <X />
              </button>
            </header>
            <div className="portal-form-progress">
              <i className="active" />
              <i className={step === 2 ? 'active' : ''} />
            </div>
            {step === 1 ? (
              <section>
                <span className="portal-form-label">游戏图标</span>
                <button
                  className="portal-icon-picker"
                  type="button"
                  onClick={() => fileInput.current?.click()}
                >
                  {draft.icon ? (
                    <Image
                      src={draft.icon}
                      width={128}
                      height={128}
                      unoptimized
                      alt="游戏 Icon"
                    />
                  ) : uploading ? (
                    <LoaderCircle className="is-spinning" />
                  ) : (
                    <ImagePlus />
                  )}
                </button>
                <small>建议尺寸 128 × 128 px，支持 JPG、PNG、WebP</small>
                <span className="portal-form-label">首页横幅（可选）</span>
                <div className="portal-banner-field">
                  <button
                    className="portal-banner-picker"
                    type="button"
                    onClick={() => bannerInput.current?.click()}
                  >
                    {draft.banner ? (
                      <Image
                        src={draft.banner}
                        width={480}
                        height={160}
                        unoptimized
                        alt="游戏 Banner"
                      />
                    ) : bannerUploading ? (
                      <LoaderCircle className="is-spinning" />
                    ) : (
                      <>
                        <ImagePlus />
                        <span>上传 Banner</span>
                      </>
                    )}
                  </button>
                  {draft.banner ? (
                    <button
                      className="portal-banner-clear"
                      type="button"
                      onClick={() => setDraft((value) => ({ ...value, banner: '' }))}
                    >
                      清除 Banner
                    </button>
                  ) : null}
                </div>
                <small>可选；建议宽图，支持 JPG、PNG、WebP</small>
                <label htmlFor="portal-game-name">游戏名称</label>
                <input
                  id="portal-game-name"
                  value={draft.name}
                  onChange={(event) =>
                    setDraft({ ...draft, name: event.target.value })
                  }
                  placeholder="请输入想创建的游戏"
                />
                <button
                  className="portal-form-primary"
                  type="button"
                  disabled={!draft.name.trim() || !draft.icon}
                  onClick={() => setStep(2)}
                >
                  下一步
                </button>
              </section>
            ) : (
              <section>
                <label htmlFor="portal-game-domain">自定义 Wiki 域名</label>
                <div className="portal-domain-field">
                  <span>本站域名/</span>
                  <input
                    id="portal-game-domain"
                    value={draft.domain}
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        domain: event.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9-]/g, ''),
                      })
                    }
                    placeholder="game-name"
                  />
                </div>
                <small>
                  支持小写字母、数字和连字符，长度 2–32 位，必须包含字母。
                </small>
                {domain && !domainValid && (
                  <p className="portal-domain-error">域名格式不正确。</p>
                )}
                {domainTaken && (
                  <p className="portal-domain-error">
                    该域名已被占用，请更换。
                  </p>
                )}
                {formMode === 'edit' && domain !== editingId && (
                  <p className="portal-domain-warning">
                    修改后，原 Wiki 地址将失效。
                  </p>
                )}
                <div className="portal-form-buttons">
                  <button type="button" onClick={() => setStep(1)}>
                    上一步
                  </button>
                  <button
                    className="portal-form-primary"
                    type="button"
                    disabled={!domainValid || domainTaken || saving}
                    onClick={() => void submit()}
                  >
                    {saving
                      ? '保存中…'
                      : formMode === 'edit'
                        ? '保存修改'
                        : '创建 Wiki'}
                  </button>
                </div>
              </section>
            )}
            <input
              ref={fileInput}
              hidden
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
              onChange={(event) => void upload(event.target.files?.[0])}
            />
            <input
              ref={bannerInput}
              hidden
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
              onChange={(event) => void uploadBanner(event.target.files?.[0])}
            />
          </dialog>
        </div>
      )}
    </>
  );
}
